// Reference for this implementation:
// https://gitlab.share-now.com/cuba/barracuda/customer-onboarding-ui/-/blob/main/src/utils/scripts.ts
import type { SupportedSript, TrackingScript, UsercentricsScript } from '@meeseeks/centaur-tracking'

/**
 * Builds a script element for tracking based on the provided scriptObject, incorporating given script properties.
 * This function is used to create the script element used in the <head> tag of our webpage based on a script
 * created using meseeks helper library (see https://gitlab.share-now.com/meeseeks/manicorn-tracking#usage).
 * @param {SupportedSript} scriptObject - The script object created by tracking library containing properties for building the script element.
 * @returns {HTMLScriptElement} - The newly created script element.
 */
export function buildScriptElement(scriptObject: SupportedSript) {
  const el = document.createElement('script')

  // Extracting properties from the scriptObject
  const { src, innerHTML, id, async, defer, 'data-test-id': dataTestId } = scriptObject
  if (src)
    el.src = src
  if (innerHTML)
    el.innerHTML = innerHTML
  if (id)
    el.id = id
  if (async)
    el.async = async
  if (defer)
    el.defer = defer
  if (dataTestId)
    el.setAttribute('data-test-id', dataTestId)

  // UsercentricsScript
  if ('data-settings-id' in scriptObject && 'type' in scriptObject) {
    const { 'data-settings-id': dataSettingsId, type } = scriptObject as UsercentricsScript
    el.setAttribute('data-settings-id', dataSettingsId)
    // This attribute stores specific settings related to the Usercentrics script.
    el.type = type!
  }

  // TrackingScript
  if ('data-usercentrics' in scriptObject && 'type' in scriptObject) {
    const { 'data-usercentrics': dataUsercentrics, type } = scriptObject as TrackingScript
    // This attribute is used for managing the user's consent settings with Usercentrics.
    el.setAttribute('data-usercentrics', dataUsercentrics)
    el.type = type!
  }
  return el
}

export function addScriptElement(el: HTMLScriptElement) {
  document.head.appendChild(el)
}

export function isRunningOnIntHost() {
  return window.location.hostname.includes('int.')
}

export function isRunningOnLocalHost() {
  return window.location.hostname === 'localhost'
}

export function isRunningOnFree2MoveDomain(): boolean {
  if (typeof window !== 'undefined') {
    return window.location.hostname.includes('free2move')
  }
  else {
    if (process.dev) {
      return false // or false, based on your development needs
    }
    const headers = useRequestHeaders()
    const host = headers.host || ''
    return host.includes('free2move')
  }
}

// script is retrieved from https://insights.hotjar.com/site/list
export function buildHotjarScript(hotjarId: string): string {
  return `(function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:${hotjarId},hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`
}
